import React, { Fragment } from 'react';
import { View, TouchableOpacity } from 'react-native';

import { isFunction as _isFunction, map as _map, isNil as _isNil } from 'lodash';
import PropTypes from 'prop-types';

import { Icon, VFButton, VFText } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import { TABS_LABELS } from '../../../Requests/Utils/Enums';
import { getThemeImages as getSohoThemeImages } from '../../../Themes/Modes';
import SohoFrameworkTestKeys from '../../../Utils/SohoFrameworkTestKeys';
import RequestBodyItem from '../RequestBodyItem';

import styles from './RequestCard.Styles';

const RequestCard = ({
  title,
  icon,
  primaryButtonTitle,
  onPrimaryButtonPress,
  onSecondaryButtonPress,
  secondaryButtonTitle,
  users,
  withViewDetails,
  onViewDetailsPress,
  renderMainSubTitle,
  tabKey,
  isSohoUser,
  footerArray,
  activeLicencesCount,
  licencesCount,
}) => {
  const theme = useTheme();
  const sohoImages = getSohoThemeImages(theme.name);

  const renderCardButtons = () => {
    return (
      <View style={styles.btnContainer}>
        <VFButton
          title={primaryButtonTitle}
          style={styles.btnStyle}
          testKey={SohoFrameworkTestKeys.RequestsCardPrimaryBtn}
          onPress={_isFunction(onPrimaryButtonPress) && onPrimaryButtonPress}
          textStyle={styles.btnTextStyle}
        />
        {!!secondaryButtonTitle && (
          <VFButton
            textStyle={styles.btnTextStyle}
            style={styles.btnStyle}
            onPress={_isFunction(onSecondaryButtonPress) && onSecondaryButtonPress}
            title={secondaryButtonTitle}
            type="secondary"
          />
        )}
      </View>
    );
  };

  const renderUsers = () => {
    if (users?.length === 0) {
      return;
    }
    return _map(users, (user, index) => {
      return (
        <Fragment key={index}>
          <RequestBodyItem
            title={user.name}
            subtitle={user.subtitle}
            status={user.requestStatus}
            subtitlePlaceHolders={user.subtitlePlaceHolders}
            renderRightSideContent={user.renderRightSideContent}
            isSohoUser={isSohoUser}
          />
          {index === users.length - 1 ? null : <View style={styles.separator} />}
        </Fragment>
      );
    });
  };

  const renderFooterArray = () => {
    return (
      <>
        <View style={styles.separator} />
        <View style={styles.marginHorizontal}>
          <VFText
            i18nKey="soho_requests_application_components_image_title"
            style={styles.footerTextStyle}
          />
          <View style={[styles.mainSection, styles.iconsArrayStyle]}>
            {_map(footerArray, footerItem => {
              return <Icon name={footerItem} type="image" size={50} />;
            })}
          </View>
        </View>
      </>
    );
  };

  return (
    <View style={styles.cardContainerStyle(theme)}>
      <View style={styles.marginHorizontal}>
        <View style={styles.mainSection}>
          <View style={styles.icon}>
            <Icon
              style={[styles.leftIcon]}
              name={icon ?? sohoImages.ic_admin}
              type="image"
              size={41}
            />
          </View>
          <View style={styles.mainSectionText}>
            <VFText style={[styles.topText]} i18nKey={title} numberOfLines={2} />
            {_isFunction(renderMainSubTitle) && renderMainSubTitle()}
          </View>
        </View>

        <View style={[styles.separator, isSohoUser ? styles.marginVertical : {}]} />
        <View style={styles.userContainer}>{renderUsers()}</View>
        <View style={[styles.separator, isSohoUser ? styles.marginVertical : {}]} />

        {withViewDetails && (
          <View style={styles.viewDetailsContainer}>
            <TouchableOpacity
              testID={SohoFrameworkTestKeys.RequestsCardViewDetailsBtn}
              onPress={_isFunction(onViewDetailsPress) ? onViewDetailsPress : () => null}
            >
              <VFText
                i18nKey="soho_requests_view_details_button_title"
                style={styles.viewDetailsStyle}
              />
            </TouchableOpacity>
            {!_isNil(licencesCount) && !_isNil(activeLicencesCount) && (
              <VFText
                i18nKey="soho_requests_licences_used_count_format"
                placeHolders={[`${activeLicencesCount}/${licencesCount}`]}
                style={styles.licencesCountTextStyle}
              />
            )}
          </View>
        )}
        {tabKey === TABS_LABELS.PENDING ? renderCardButtons() : null}
      </View>
      {!_isNil(footerArray) && renderFooterArray()}
    </View>
  );
};
RequestCard.defaultProps = {
  users: [],
  withViewDetails: true,
};

RequestCard.propTypes = {
  users: PropTypes.array,
  title: PropTypes.string,
  type: PropTypes.string,
  tabKey: PropTypes.oneOf(TABS_LABELS),
  images: PropTypes.object,
  icon: PropTypes.string,
  withViewDetails: PropTypes.bool,
  requestPrice: PropTypes.string,
  primaryButtonTitle: PropTypes.string,
  secondaryButtonTitle: PropTypes.string,
  onPrimaryButtonPress: PropTypes.func,
  onViewDetailsPress: PropTypes.func,
  onSecondaryButtonPress: PropTypes.func,
  renderMainSubTitle: PropTypes.func,
  requestUnitMeasure: PropTypes.string,
  isSohoUser: PropTypes.bool,
  footerArray: PropTypes.arrayOf(PropTypes.object),
  activeLicencesCount: PropTypes.string,
  licencesCount: PropTypes.string,
};
export default RequestCard;
